/**
 * ProcessApprovalQueue widget — items awaiting the current user's approval.
 *
 * Migrated to live API in Wave 3.6. Reads GET /v1/processes/requests
 * filtered to status=pending. In the process model, a ProcessRequest is
 * the thing that queues up for approval (a ProcessTask is the individual
 * assigned work unit within that request). The mock conflated the two;
 * the live widget surfaces the requests so the [Approve]/[Reject]
 * buttons map onto the backend's /requests/{id}/approve|reject endpoints.
 *
 * Per-row actions are wired but gated behind disabled styling so
 * clicking is visibly dead — the real approve/reject flow lives on the
 * Requests page where the operator can see request details + attach a
 * comment. The widget's job is a glance, not a commit-from-dashboard.
 *
 * Priority chips and age (submitted_at → '3h' / '2d' / etc) come from
 * the typed ProcessRequest fields.
 */

import React, { useEffect, useState } from 'react';
import {
  Avatar, Box, Button, Chip, CircularProgress, List, ListItem, Paper,
  Tooltip, Typography,
} from '@mui/material';
import { CheckOutlined, CloseOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiError, processesApi } from '../../../../api';
import { WidgetErrorState } from '../../../../components/primitives';
import { isActionableRequestStatus } from '../../../../api/processes';
import type { ProcessRequest } from '../../../../api/processes';
import { useTranslation } from 'react-i18next';
import { formatRelativeTime, translatePriority } from './widgetDisplay';
import WidgetSummaryLine from './WidgetSummaryLine';
import { translateApiError } from '../../../../i18n/translateApiError';
import WidgetEmptyState from '../../../../components/primitives/WidgetEmptyState';
import { TaskAltOutlined } from '@mui/icons-material';

import { getSystemRequestTitle } from '../../../Processes/processDisplay';

function avatarColor(seed: string): string {
  const palette = ['#2196F3', '#4CAF50', '#FF9800', '#9C27B0', '#F44336', '#00BCD4', '#795548'];
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return palette[Math.abs(hash) % palette.length];
}

function initials(seed: string | null | undefined): string {
  if (!seed) return '•';
  return seed.slice(0, 2).toUpperCase();
}

function isUrgent(req: ProcessRequest): boolean {
  if (req.priority === 'high' || req.priority === 'urgent') return true;
  if (req.due_date) {
    const due = Date.parse(req.due_date);
    // Within 24h counts as urgent even if priority wasn't flagged.
    return Number.isFinite(due) && due - Date.now() <= 24 * 3_600_000;
  }
  return false;
}

const ProcessApprovalQueue: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [requests, setRequests] = useState<ProcessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // `assigned_to: 'me'` is the server's own filter
        // (`processes.service.request_approver_clause`: named approver OR an
        // eligible seat on a live task, excluding one's own requests) — that is
        // what makes the "your decision" copy above true.
        //
        // The status GROUP stays client-side: the backend matches `status`
        // exactly, so any single value would drop part of the actionable set
        // (svdy-ui §12.3). This widget previously sent `status: 'pending'`,
        // a value `ProcessRequest.status` never holds, and showed nothing at all.
        const rows = await processesApi.processRequestsApi.list({ assigned_to: 'me' });
        if (cancelled) return;
        const actionable = rows.filter((r) => isActionableRequestStatus(r.status));
        // Most-recently-submitted first. The WHOLE actionable list goes into
        // state and the slice happens at render, so the summary line states the
        // real backlog. Same defect the sibling AttendanceApprovalQueue had
        // (see its count.test.tsx): slicing into state made a 12-deep queue
        // read "8 pending" beside a page that said 12.
        const sorted = [...actionable].sort((a, b) => {
          const ap = a.submitted_at ? new Date(a.submitted_at).getTime() : 0;
          const bp = b.submitted_at ? new Date(b.submitted_at).getTime() : 0;
          return bp - ap;
        });
        setRequests(sorted);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof ApiError ? translateApiError(e, t) : t('dashboard:widgets.approvalQueue.loadError'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t]);

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <WidgetErrorState error={error} />;
  }

  if (requests.length === 0) {
    return (
      <WidgetEmptyState icon={TaskAltOutlined} title={t('dashboard:widgets.approvalQueue.noApprovals')} />
    );
  }

  const urgentCount = requests.filter(isUrgent).length;
  // The frame holds eight rows; the counts above deliberately do not.
  const drawn = requests.slice(0, 8);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <WidgetSummaryLine
        action={urgentCount > 0 ? (
          <Chip label={t('dashboard:widgets.approvalQueue.urgentCount', { count: urgentCount })} size="small" color="warning" sx={{ height: 20, fontSize: '0.75rem' }} />
        ) : undefined}
      >
        {t('dashboard:widgets.approvalQueue.pendingCount', { count: requests.length })}
      </WidgetSummaryLine>
      <List sx={{ py: 0, flex: 1, overflow: 'auto' }}>
        {drawn.map((r, i) => {
          const urgent = isUrgent(r);
          const requesterLabel = r.requester_id
            ? t('dashboard:widgets.approvalQueue.requester', { id: r.requester_id.slice(0, 8) })
            : t('dashboard:widgets.approvalQueue.unknownRequester');
          return (
            <ListItem
              key={r.id}
              component={Paper}
              elevation={0}
              // Same correction as MyPendingTasks (see the long note there):
              // `/processes/requests/pending` was never a route, and `/me/approvals`
              // is a real route that no admin-tier role can reach —
              // `resolveRoleLanding` redirects every `/me/*` path to /dashboard
              // with `replace`, so the click leaves no trace in the URL. This
              // widget renders in the admin shell, so it lands on the request's
              // own process instance, which is reachable and more useful than a
              // personal approvals inbox.
              onClick={() => navigate(
                r.instance_id
                  ? `/processes/instances/${r.instance_id}`
                  : '/processes/instances/active',
              )}
              sx={{ py: 1, mb: i < drawn.length - 1 ? 0.5 : 0,
                borderRadius: 1, bgcolor: 'background.paper', display: 'flex', alignItems: 'center', gap: 1,
                cursor: 'pointer',
                border: '1px solid transparent',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: 'divider' },
              }}
            >
              <Avatar sx={{ width: 28, height: 28, bgcolor: avatarColor(r.requester_id || r.id),
                fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>
                {initials(r.requester_id)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2"
                  sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>
                  {getSystemRequestTitle(t, r, r.title)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {requesterLabel}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>·</Typography>
                  <Chip label={translatePriority(t, r.priority)} size="small"
                    sx={{ height: 16, fontSize: '0.75rem', '& .MuiChip-label': { px: 0.75 } }} />
                </Box>
              </Box>
              {/* The date is a ROW COLUMN, a sibling of the text block and the buttons.
                 *
                 * It used to live INSIDE the text block's second line, pushed right with
                 * `ml: 'auto'`. That made its right edge the TEXT BLOCK's edge — i.e. a function of
                 * however wide the buttons happened to be — and put it on the second line while the
                 * buttons were centred across both. So it lined up with nothing, which is what the
                 * owner reported. As its own column it right-aligns in a fixed track, so every row's
                 * date shares one edge and that edge is a real column boundary.
                 */}
              <Typography variant="caption"
                color={urgent ? 'warning.main' : 'text.secondary'}
                sx={{ fontSize: '0.75rem', flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {formatRelativeTime(r.submitted_at ?? r.created_at, t, i18n.language)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                <Tooltip title={t('dashboard:widgets.processApprovalQueue.openToApprove')}>
                  <Button size="small" variant="outlined" color="success"
                    sx={{ minWidth: 0, px: 0.75, py: 0.25, borderRadius: 1, fontSize: '0.75rem', height: 24 }}>
                    <CheckOutlined sx={{ fontSize: '0.85rem' }} />
                  </Button>
                </Tooltip>
                <Tooltip title={t('dashboard:widgets.processApprovalQueue.openToReject')}>
                  <Button size="small" variant="outlined" color="error"
                    sx={{ minWidth: 0, px: 0.75, py: 0.25, borderRadius: 1, fontSize: '0.75rem', height: 24 }}>
                    <CloseOutlined sx={{ fontSize: '0.85rem' }} />
                  </Button>
                </Tooltip>
              </Box>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default ProcessApprovalQueue;
